"use client";;
import {
  Check,
  File,
  FileArchive,
  FileAudio,
  FileImage,
  FileSpreadsheet,
  FileText,
  FileVideo,
  Presentation,
  Upload,
  X,
} from "lucide-react";
import * as React from "react";
import { toast } from "sonner";
import { DataGridCellWrapper } from "@/components/data-grid/data-grid-cell-wrapper";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { useBadgeOverflow } from "@/hooks/use-badge-overflow";
import { useDebouncedCallback } from "@/hooks/use-debounced-callback";
import { getCellKey, getLineCount } from "@/lib/data-grid";
import { cn } from "@/lib/utils/index";

export function ShortTextCell(
  {
    cell,
    tableMeta,
    rowIndex,
    columnId,
    rowHeight,
    isEditing,
    isFocused,
    isSelected,
    isSearchMatch,
    isActiveSearchMatch,
    readOnly
  }
) {
  const initialValue = cell.getValue();
  const [value, setValue] = React.useState(initialValue);
  const cellRef = React.useRef(null);
  const containerRef = React.useRef(null);

  const prevInitialValueRef = React.useRef(initialValue);
  if (initialValue !== prevInitialValueRef.current) {
    prevInitialValueRef.current = initialValue;
    setValue(initialValue);
    if (cellRef.current && !isEditing) {
      cellRef.current.textContent = initialValue;
    }
  }

  const onBlur = React.useCallback(() => {
    // Read the current value directly from the DOM to avoid stale state
    const currentValue = cellRef.current?.textContent ?? "";
    if (!readOnly && currentValue !== initialValue) {
      tableMeta?.onDataUpdate?.({ rowIndex, columnId, value: currentValue });
    }
    tableMeta?.onCellEditingStop?.();
  }, [tableMeta, rowIndex, columnId, initialValue, readOnly]);

  const onInput = React.useCallback((event) => {
    const currentValue = event.currentTarget.textContent ?? "";
    setValue(currentValue);
  }, []);

  const onWrapperKeyDown = React.useCallback((event) => {
    if (isEditing) {
      if (event.key === "Enter") {
        event.preventDefault();
        const currentValue = cellRef.current?.textContent ?? "";
        if (currentValue !== initialValue) {
          tableMeta?.onDataUpdate?.({
            rowIndex,
            columnId,
            value: currentValue,
          });
        }
        tableMeta?.onCellEditingStop?.({ moveToNextRow: true });
      } else if (event.key === "Tab") {
        event.preventDefault();
        const currentValue = cellRef.current?.textContent ?? "";
        if (currentValue !== initialValue) {
          tableMeta?.onDataUpdate?.({
            rowIndex,
            columnId,
            value: currentValue,
          });
        }
        tableMeta?.onCellEditingStop?.({
          direction: event.shiftKey ? "left" : "right",
        });
      } else if (event.key === "Escape") {
        event.preventDefault();
        setValue(initialValue);
        cellRef.current?.blur();
      }
    } else if (
      isFocused &&
      event.key.length === 1 &&
      !event.ctrlKey &&
      !event.metaKey
    ) {
      // Handle typing to pre-fill the value when editing starts
      setValue(event.key);

      queueMicrotask(() => {
        if (cellRef.current && cellRef.current.contentEditable === "true") {
          cellRef.current.textContent = event.key;
          const range = document.createRange();
          const selection = window.getSelection();
          range.selectNodeContents(cellRef.current);
          range.collapse(false);
          selection?.removeAllRanges();
          selection?.addRange(range);
        }
      });
    }
  }, [isEditing, isFocused, initialValue, tableMeta, rowIndex, columnId]);

  React.useEffect(() => {
    if (isEditing && cellRef.current) {
      cellRef.current.focus();

      if (!cellRef.current.textContent && value) {
        cellRef.current.textContent = value;
      }

      if (cellRef.current.textContent) {
        const range = document.createRange();
        const selection = window.getSelection();
        range.selectNodeContents(cellRef.current);
        range.collapse(false);
        selection?.removeAllRanges();
        selection?.addRange(range);
      }
    }
  }, [isEditing, value]);

  const displayValue = !isEditing ? (value ?? "") : "";

  return (
    <DataGridCellWrapper
      ref={containerRef}
      cell={cell}
      tableMeta={tableMeta}
      rowIndex={rowIndex}
      columnId={columnId}
      rowHeight={rowHeight}
      isEditing={isEditing}
      isFocused={isFocused}
      isSelected={isSelected}
      isSearchMatch={isSearchMatch}
      isActiveSearchMatch={isActiveSearchMatch}
      readOnly={readOnly}
      onKeyDown={onWrapperKeyDown}>
      <div
        role="textbox"
        data-slot="grid-cell-content"
        contentEditable={isEditing}
        tabIndex={-1}
        ref={cellRef}
        onBlur={onBlur}
        onInput={onInput}
        suppressContentEditableWarning
        className={cn("size-full overflow-hidden outline-none", {
          "whitespace-nowrap **:inline **:whitespace-nowrap [&_br]:hidden":
            isEditing,
        })}>
        {displayValue}
      </div>
    </DataGridCellWrapper>
  );
}

export function LongTextCell(
  {
    cell,
    tableMeta,
    rowIndex,
    columnId,
    rowHeight,
    isFocused,
    isEditing,
    isSelected,
    isSearchMatch,
    isActiveSearchMatch,
    readOnly
  }
) {
  const initialValue = cell.getValue();
  const [value, setValue] = React.useState(initialValue ?? "");
  const textareaRef = React.useRef(null);
  const containerRef = React.useRef(null);
  const sideOffset = -(containerRef.current?.clientHeight ?? 0);

  const prevInitialValueRef = React.useRef(initialValue);
  if (initialValue !== prevInitialValueRef.current) {
    prevInitialValueRef.current = initialValue;
    setValue(initialValue ?? "");
  }

  // Debounced auto-save (300ms delay)
  const debouncedSave = useDebouncedCallback((newValue) => {
    if (!readOnly) {
      tableMeta?.onDataUpdate?.({ rowIndex, columnId, value: newValue });
    }
  }, 300);

  const onSave = React.useCallback(() => {
    // Immediately save any pending changes and close the popover
    if (!readOnly && value !== initialValue) {
      tableMeta?.onDataUpdate?.({ rowIndex, columnId, value });
    }
    tableMeta?.onCellEditingStop?.();
  }, [tableMeta, value, initialValue, rowIndex, columnId, readOnly]);

  const onCancel = React.useCallback(() => {
    // Restore the original value
    setValue(initialValue ?? "");
    if (!readOnly) {
      tableMeta?.onDataUpdate?.({ rowIndex, columnId, value: initialValue });
    }
    tableMeta?.onCellEditingStop?.();
  }, [tableMeta, initialValue, rowIndex, columnId, readOnly]);

  const onOpenChange = React.useCallback((isOpen) => {
    if (isOpen && !readOnly) {
      tableMeta?.onCellEditingStart?.(rowIndex, columnId);
    } else {
      // Immediately save any pending changes when closing
      if (!readOnly && value !== initialValue) {
        tableMeta?.onDataUpdate?.({ rowIndex, columnId, value });
      }
      tableMeta?.onCellEditingStop?.();
    }
  }, [tableMeta, value, initialValue, rowIndex, columnId, readOnly]);

  const onOpenAutoFocus = React.useCallback((event) => {
    event.preventDefault();
    if (textareaRef.current) {
      textareaRef.current.focus();
      const length = textareaRef.current.value.length;
      textareaRef.current.setSelectionRange(length, length);
    }
  }, []);

  const onBlur = React.useCallback(() => {
    // Immediately save any pending changes on blur
    if (!readOnly && value !== initialValue) {
      tableMeta?.onDataUpdate?.({ rowIndex, columnId, value });
    }
    tableMeta?.onCellEditingStop?.();
  }, [tableMeta, value, initialValue, rowIndex, columnId, readOnly]);

  const onChange = React.useCallback((event) => {
    const newValue = event.target.value;
    setValue(newValue);
    // Debounced auto-save
    debouncedSave(newValue);
  }, [debouncedSave]);

  const onKeyDown = React.useCallback((event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      onCancel();
    } else if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      onSave();
    } else if (event.key === "Tab") {
      event.preventDefault();
      // Save any pending changes
      if (value !== initialValue) {
        tableMeta?.onDataUpdate?.({ rowIndex, columnId, value });
      }
      tableMeta?.onCellEditingStop?.({
        direction: event.shiftKey ? "left" : "right",
      });
      return;
    }
    // Stop propagation to prevent grid navigation
    event.stopPropagation();
  }, [onSave, onCancel, value, initialValue, tableMeta, rowIndex, columnId]);

  return (
    <Popover open={isEditing} onOpenChange={onOpenChange}>
      <PopoverAnchor asChild>
        <DataGridCellWrapper
          ref={containerRef}
          cell={cell}
          tableMeta={tableMeta}
          rowIndex={rowIndex}
          columnId={columnId}
          rowHeight={rowHeight}
          isEditing={isEditing}
          isFocused={isFocused}
          isSelected={isSelected}
          isSearchMatch={isSearchMatch}
          isActiveSearchMatch={isActiveSearchMatch}
          readOnly={readOnly}>
          <span data-slot="grid-cell-content">{value}</span>
        </DataGridCellWrapper>
      </PopoverAnchor>
      <PopoverContent
        data-grid-cell-editor=""
        align="start"
        side="bottom"
        sideOffset={sideOffset}
        className="w-[400px] rounded-none p-0"
        onOpenAutoFocus={onOpenAutoFocus}>
        <Textarea
          placeholder="Enter text..."
          className="min-h-[150px] resize-none rounded-none border-0 shadow-none focus-visible:ring-0"
          ref={textareaRef}
          value={value}
          onBlur={onBlur}
          onChange={onChange}
          onKeyDown={onKeyDown} />
      </PopoverContent>
    </Popover>
  );
}

export function NumberCell(
  {
    cell,
    tableMeta,
    rowIndex,
    columnId,
    rowHeight,
    isFocused,
    isEditing,
    isSelected,
    isSearchMatch,
    isActiveSearchMatch,
    readOnly
  }
) {
  const initialValue = cell.getValue();
  const [value, setValue] = React.useState(String(initialValue ?? ""));
  const inputRef = React.useRef(null);
  const containerRef = React.useRef(null);
  const cellOpts = cell.column.columnDef.meta?.cell;
  const numberCellOpts = cellOpts?.variant === "number" ? cellOpts : null;
  const min = numberCellOpts?.min;
  const max = numberCellOpts?.max;
  const step = numberCellOpts?.step;

  const prevInitialValueRef = React.useRef(initialValue);
  if (initialValue !== prevInitialValueRef.current) {
    prevInitialValueRef.current = initialValue;
    setValue(String(initialValue ?? ""));
  }

  const onBlur = React.useCallback(() => {
    const numValue = value === "" ? null : Number(value);
    if (!readOnly && numValue !== initialValue) {
      tableMeta?.onDataUpdate?.({ rowIndex, columnId, value: numValue });
    }
    tableMeta?.onCellEditingStop?.();
  }, [tableMeta, rowIndex, columnId, initialValue, value, readOnly]);

  const onChange = React.useCallback((event) => {
    setValue(event.target.value);
  }, []);

  const onWrapperKeyDown = React.useCallback((event) => {
    if (isEditing) {
      if (event.key === "Enter") {
        event.preventDefault();
        const numValue = value === "" ? null : Number(value);
        if (numValue !== initialValue) {
          tableMeta?.onDataUpdate?.({ rowIndex, columnId, value: numValue });
        }
        tableMeta?.onCellEditingStop?.({ moveToNextRow: true });
      } else if (event.key === "Tab") {
        event.preventDefault();
        const numValue = value === "" ? null : Number(value);
        if (numValue !== initialValue) {
          tableMeta?.onDataUpdate?.({ rowIndex, columnId, value: numValue });
        }
        tableMeta?.onCellEditingStop?.({
          direction: event.shiftKey ? "left" : "right",
        });
      } else if (event.key === "Escape") {
        event.preventDefault();
        setValue(String(initialValue ?? ""));
        inputRef.current?.blur();
      }
    } else if (isFocused) {
      // Handle Backspace to start editing with empty value
      if (event.key === "Backspace") {
        setValue("");
      } else if (event.key.length === 1 && !event.ctrlKey && !event.metaKey) {
        // Handle typing to pre-fill the value when editing starts
        setValue(event.key);
      }
    }
  }, [isEditing, isFocused, initialValue, tableMeta, rowIndex, columnId, value]);

  React.useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  return (
    <DataGridCellWrapper
      ref={containerRef}
      cell={cell}
      tableMeta={tableMeta}
      rowIndex={rowIndex}
      columnId={columnId}
      rowHeight={rowHeight}
      isEditing={isEditing}
      isFocused={isFocused}
      isSelected={isSelected}
      isSearchMatch={isSearchMatch}
      isActiveSearchMatch={isActiveSearchMatch}
      readOnly={readOnly}
      onKeyDown={onWrapperKeyDown}>
      {isEditing ? (
        <input
          ref={inputRef}
          type="number"
          value={value}
          min={min}
          max={max}
          step={step}
          onBlur={onBlur}
          onChange={onChange}
          className="w-full border-none bg-transparent p-0 outline-none" />
      ) : (
        <span data-slot="grid-cell-content">{value}</span>
      )}
    </DataGridCellWrapper>
  );
}

function getUrlHref(urlString) {
  if (!urlString || urlString.trim() === "") return "";

  const trimmed = urlString.trim();

  // Reject dangerous protocols (extra safety, though our http:// prefix would neutralize them)
  if (/^(javascript|data|vbscript|file):/i.test(trimmed)) {
    return "";
  }

  // Check if it already has a protocol
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
    return trimmed;
  }

  // Add http:// prefix for links without protocol
  return `http://${trimmed}`;
}

export function UrlCell(
  {
    cell,
    tableMeta,
    rowIndex,
    columnId,
    rowHeight,
    isEditing,
    isFocused,
    isSelected,
    isSearchMatch,
    isActiveSearchMatch,
    readOnly
  }
) {
  const initialValue = cell.getValue();
  const [value, setValue] = React.useState(initialValue ?? "");
  const cellRef = React.useRef(null);
  const containerRef = React.useRef(null);

  const prevInitialValueRef = React.useRef(initialValue);
  if (initialValue !== prevInitialValueRef.current) {
    prevInitialValueRef.current = initialValue;
    setValue(initialValue ?? "");
    if (cellRef.current && !isEditing) {
      cellRef.current.textContent = initialValue ?? "";
    }
  }

  const onBlur = React.useCallback(() => {
    const currentValue = cellRef.current?.textContent?.trim() ?? "";

    if (!readOnly && currentValue !== initialValue) {
      tableMeta?.onDataUpdate?.({
        rowIndex,
        columnId,
        value: currentValue || null,
      });
    }
    tableMeta?.onCellEditingStop?.();
  }, [tableMeta, rowIndex, columnId, initialValue, readOnly]);

  const onInput = React.useCallback((event) => {
    const currentValue = event.currentTarget.textContent ?? "";
    setValue(currentValue);
  }, []);

  const onWrapperKeyDown = React.useCallback((event) => {
    if (isEditing) {
      if (event.key === "Enter") {
        event.preventDefault();
        const currentValue = cellRef.current?.textContent?.trim() ?? "";
        if (!readOnly && currentValue !== initialValue) {
          tableMeta?.onDataUpdate?.({
            rowIndex,
            columnId,
            value: currentValue || null,
          });
        }
        tableMeta?.onCellEditingStop?.({ moveToNextRow: true });
      } else if (event.key === "Tab") {
        event.preventDefault();
        const currentValue = cellRef.current?.textContent?.trim() ?? "";
        if (!readOnly && currentValue !== initialValue) {
          tableMeta?.onDataUpdate?.({
            rowIndex,
            columnId,
            value: currentValue || null,
          });
        }
        tableMeta?.onCellEditingStop?.({
          direction: event.shiftKey ? "left" : "right",
        });
      } else if (event.key === "Escape") {
        event.preventDefault();
        setValue(initialValue ?? "");
        cellRef.current?.blur();
      }
    } else if (
      isFocused &&
      !readOnly &&
      event.key.length === 1 &&
      !event.ctrlKey &&
      !event.metaKey
    ) {
      // Handle typing to pre-fill the value when editing starts
      setValue(event.key);

      queueMicrotask(() => {
        if (cellRef.current && cellRef.current.contentEditable === "true") {
          cellRef.current.textContent = event.key;
          const range = document.createRange();
          const selection = window.getSelection();
          range.selectNodeContents(cellRef.current);
          range.collapse(false);
          selection?.removeAllRanges();
          selection?.addRange(range);
        }
      });
    }
  }, [
    isEditing,
    isFocused,
    initialValue,
    tableMeta,
    rowIndex,
    columnId,
    readOnly,
  ]);

  const onLinkClick = React.useCallback((event) => {
    if (isEditing) {
      event.preventDefault();
      return;
    }

    // Check if URL was rejected due to dangerous protocol
    const href = getUrlHref(value);
    if (!href) {
      event.preventDefault();
      toast.error("Invalid URL", {
        description:
          "URL contains a dangerous protocol (javascript:, data:, vbscript:, or file:)",
      });
      return;
    }

    // Stop propagation to prevent grid from interfering with link navigation
    event.stopPropagation();
  }, [isEditing, value]);

  React.useEffect(() => {
    if (isEditing && cellRef.current) {
      cellRef.current.focus();

      if (!cellRef.current.textContent && value) {
        cellRef.current.textContent = value;
      }

      if (cellRef.current.textContent) {
        const range = document.createRange();
        const selection = window.getSelection();
        range.selectNodeContents(cellRef.current);
        range.collapse(false);
        selection?.removeAllRanges();
        selection?.addRange(range);
      }
    }
  }, [isEditing, value]);

  const displayValue = !isEditing ? (value ?? "") : "";
  const urlHref = displayValue ? getUrlHref(displayValue) : "";
  const isDangerousUrl = displayValue && !urlHref;

  return (
    <DataGridCellWrapper
      ref={containerRef}
      cell={cell}
      tableMeta={tableMeta}
      rowIndex={rowIndex}
      columnId={columnId}
      rowHeight={rowHeight}
      isEditing={isEditing}
      isFocused={isFocused}
      isSelected={isSelected}
      isSearchMatch={isSearchMatch}
      isActiveSearchMatch={isActiveSearchMatch}
      readOnly={readOnly}
      onKeyDown={onWrapperKeyDown}>
      {!isEditing && displayValue ? (
        <div data-slot="grid-cell-content" className="size-full overflow-hidden">
          <a
            data-focused={isFocused && !isDangerousUrl ? "" : undefined}
            data-invalid={isDangerousUrl ? "" : undefined}
            href={urlHref}
            target="_blank"
            rel="noopener noreferrer"
            className="truncate text-primary underline decoration-primary/30 underline-offset-2 hover:decoration-primary/60 data-invalid:cursor-not-allowed data-focused:text-foreground data-invalid:text-destructive data-focused:decoration-foreground/50 data-invalid:decoration-destructive/50 data-focused:hover:decoration-foreground/70 data-invalid:hover:decoration-destructive/70"
            onClick={onLinkClick}>
            {displayValue}
          </a>
        </div>
      ) : (
        <div
          role="textbox"
          data-slot="grid-cell-content"
          contentEditable={isEditing}
          tabIndex={-1}
          ref={cellRef}
          onBlur={onBlur}
          onInput={onInput}
          suppressContentEditableWarning
          className={cn("size-full overflow-hidden outline-none", {
            "whitespace-nowrap **:inline **:whitespace-nowrap [&_br]:hidden":
              isEditing,
          })}>
          {displayValue}
        </div>
      )}
    </DataGridCellWrapper>
  );
}

export function CheckboxCell(
  {
    cell,
    tableMeta,
    rowIndex,
    columnId,
    rowHeight,
    isFocused,
    isSelected,
    isSearchMatch,
    isActiveSearchMatch,
    readOnly
  }
) {
  const initialValue = cell.getValue();
  const [value, setValue] = React.useState(Boolean(initialValue));
  const containerRef = React.useRef(null);

  const prevInitialValueRef = React.useRef(initialValue);
  if (initialValue !== prevInitialValueRef.current) {
    prevInitialValueRef.current = initialValue;
    setValue(Boolean(initialValue));
  }

  const onCheckedChange = React.useCallback((checked) => {
    if (readOnly) return;
    setValue(checked);
    tableMeta?.onDataUpdate?.({ rowIndex, columnId, value: checked });
  }, [tableMeta, rowIndex, columnId, readOnly]);

  const onWrapperKeyDown = React.useCallback((event) => {
    if (
      isFocused &&
      !readOnly &&
      (event.key === " " || event.key === "Enter")
    ) {
      event.preventDefault();
      event.stopPropagation();
      onCheckedChange(!value);
    } else if (isFocused && event.key === "Tab") {
      event.preventDefault();
      tableMeta?.onCellEditingStop?.({
        direction: event.shiftKey ? "left" : "right",
      });
    }
  }, [isFocused, value, onCheckedChange, tableMeta, readOnly]);

  const onWrapperClick = React.useCallback((event) => {
    if (isFocused && !readOnly) {
      event.preventDefault();
      event.stopPropagation();
      onCheckedChange(!value);
    }
  }, [isFocused, value, onCheckedChange, readOnly]);

  const onCheckboxClick = React.useCallback((event) => {
    event.stopPropagation();
  }, []);

  const onCheckboxMouseDown = React.useCallback((event) => {
    event.stopPropagation();
  }, []);

  const onCheckboxDoubleClick = React.useCallback((event) => {
    event.stopPropagation();
  }, []);

  return (
    <DataGridCellWrapper
      ref={containerRef}
      cell={cell}
      tableMeta={tableMeta}
      rowIndex={rowIndex}
      columnId={columnId}
      rowHeight={rowHeight}
      isEditing={false}
      isFocused={isFocused}
      isSelected={isSelected}
      isSearchMatch={isSearchMatch}
      isActiveSearchMatch={isActiveSearchMatch}
      readOnly={readOnly}
      className="flex size-full justify-center"
      onClick={onWrapperClick}
      onKeyDown={onWrapperKeyDown}>
      <Checkbox
        checked={value}
        onCheckedChange={onCheckedChange}
        disabled={readOnly}
        className="border-primary"
        onClick={onCheckboxClick}
        onMouseDown={onCheckboxMouseDown}
        onDoubleClick={onCheckboxDoubleClick} />
    </DataGridCellWrapper>
  );
}

export function SelectCell(
  {
    cell,
    tableMeta,
    rowIndex,
    columnId,
    rowHeight,
    isFocused,
    isEditing,
    isSelected,
    isSearchMatch,
    isActiveSearchMatch,
    readOnly
  }
) {
  const initialValue = cell.getValue();
  const [value, setValue] = React.useState(initialValue);
  const containerRef = React.useRef(null);
  const cellOpts = cell.column.columnDef.meta?.cell;
  const options = cellOpts?.variant === "select" ? cellOpts.options : [];

  const prevInitialValueRef = React.useRef(initialValue);
  if (initialValue !== prevInitialValueRef.current) {
    prevInitialValueRef.current = initialValue;
    setValue(initialValue);
  }

  const onValueChange = React.useCallback((newValue) => {
    if (readOnly) return;
    setValue(newValue);
    tableMeta?.onDataUpdate?.({ rowIndex, columnId, value: newValue });
    tableMeta?.onCellEditingStop?.();
  }, [tableMeta, rowIndex, columnId, readOnly]);

  const onOpenChange = React.useCallback((isOpen) => {
    if (isOpen && !readOnly) {
      tableMeta?.onCellEditingStart?.(rowIndex, columnId);
    } else {
      tableMeta?.onCellEditingStop?.();
    }
  }, [tableMeta, rowIndex, columnId, readOnly]);

  const onWrapperKeyDown = React.useCallback((event) => {
    if (isEditing && event.key === "Escape") {
      event.preventDefault();
      setValue(initialValue);
      tableMeta?.onCellEditingStop?.();
    } else if (!isEditing && isFocused && event.key === "Tab") {
      event.preventDefault();
      tableMeta?.onCellEditingStop?.({
        direction: event.shiftKey ? "left" : "right",
      });
    }
  }, [isEditing, isFocused, initialValue, tableMeta]);

  const displayLabel =
    options.find((opt) => opt.value === value)?.label ?? value;

  return (
    <DataGridCellWrapper
      ref={containerRef}
      cell={cell}
      tableMeta={tableMeta}
      rowIndex={rowIndex}
      columnId={columnId}
      rowHeight={rowHeight}
      isEditing={isEditing}
      isFocused={isFocused}
      isSelected={isSelected}
      isSearchMatch={isSearchMatch}
      isActiveSearchMatch={isActiveSearchMatch}
      readOnly={readOnly}
      onKeyDown={onWrapperKeyDown}>
      {isEditing ? (
        <Select
          value={value}
          onValueChange={onValueChange}
          open={isEditing}
          onOpenChange={onOpenChange}>
          <SelectTrigger
            size="sm"
            className="size-full items-start border-none p-0 shadow-none focus-visible:ring-0 dark:bg-transparent [&_svg]:hidden">
            <SelectValue />
          </SelectTrigger>
          <SelectContent
            data-grid-cell-editor=""
            // compensate for the wrapper padding
            align="start"
            alignOffset={-8}
            sideOffset={-8}
            className="min-w-[calc(var(--radix-select-trigger-width)+16px)]">
            {options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <span data-slot="grid-cell-content">{displayLabel}</span>
      )}
    </DataGridCellWrapper>
  );
}

export function MultiSelectCell(
  {
    cell,
    tableMeta,
    rowIndex,
    columnId,
    rowHeight,
    isFocused,
    isEditing,
    isSelected,
    isSearchMatch,
    isActiveSearchMatch,
    readOnly
  }
) {
  const cellValue = React.useMemo(() => {
    const value = cell.getValue();
    return value ?? [];
  }, [cell]);

  const cellKey = getCellKey(rowIndex, columnId);
  const prevCellKeyRef = React.useRef(cellKey);

  const [selectedValues, setSelectedValues] =
    React.useState(cellValue);
  const [searchValue, setSearchValue] = React.useState("");
  const containerRef = React.useRef(null);
  const inputRef = React.useRef(null);
  const cellOpts = cell.column.columnDef.meta?.cell;
  const options = cellOpts?.variant === "multi-select" ? cellOpts.options : [];
  const sideOffset = -(containerRef.current?.clientHeight ?? 0);

  const prevCellValueRef = React.useRef(cellValue);
  if (cellValue !== prevCellValueRef.current) {
    prevCellValueRef.current = cellValue;
    setSelectedValues(cellValue);
  }

  if (prevCellKeyRef.current !== cellKey) {
    prevCellKeyRef.current = cellKey;
    setSearchValue("");
  }

  const onValueChange = React.useCallback((value) => {
    if (readOnly) return;
    const newValues = selectedValues.includes(value)
      ? selectedValues.filter((v) => v !== value)
      : [...selectedValues, value];

    setSelectedValues(newValues);
    tableMeta?.onDataUpdate?.({ rowIndex, columnId, value: newValues });
    setSearchValue("");
    queueMicrotask(() => inputRef.current?.focus());
  }, [selectedValues, tableMeta, rowIndex, columnId, readOnly]);

  const removeValue = React.useCallback((valueToRemove, event) => {
    if (readOnly) return;
    event?.stopPropagation();
    event?.preventDefault();
    const newValues = selectedValues.filter((v) => v !== valueToRemove);
    setSelectedValues(newValues);
    tableMeta?.onDataUpdate?.({ rowIndex, columnId, value: newValues });
    // Focus back on input after removing
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [selectedValues, tableMeta, rowIndex, columnId, readOnly]);

  const clearAll = React.useCallback(() => {
    if (readOnly) return;
    setSelectedValues([]);
    tableMeta?.onDataUpdate?.({ rowIndex, columnId, value: [] });
    queueMicrotask(() => inputRef.current?.focus());
  }, [tableMeta, rowIndex, columnId, readOnly]);

  const onOpenChange = React.useCallback((isOpen) => {
    if (isOpen && !readOnly) {
      tableMeta?.onCellEditingStart?.(rowIndex, columnId);
    } else {
      setSearchValue("");
      tableMeta?.onCellEditingStop?.();
    }
  }, [tableMeta, rowIndex, columnId, readOnly]);

  const onOpenAutoFocus = React.useCallback((event) => {
    event.preventDefault();
    inputRef.current?.focus();
  }, []);

  const onWrapperKeyDown = React.useCallback((event) => {
    if (isEditing && event.key === "Escape") {
      event.preventDefault();
      setSelectedValues(cellValue);
      setSearchValue("");
      tableMeta?.onCellEditingStop?.();
    } else if (!isEditing && isFocused && event.key === "Tab") {
      event.preventDefault();
      setSearchValue("");
      tableMeta?.onCellEditingStop?.({
        direction: event.shiftKey ? "left" : "right",
      });
    }
  }, [isEditing, isFocused, cellValue, tableMeta]);

  const onInputKeyDown = React.useCallback((event) => {
    // Handle backspace when input is empty - remove last selected item
    if (
      event.key === "Backspace" &&
      searchValue === "" &&
      selectedValues.length > 0
    ) {
      event.preventDefault();
      const lastValue = selectedValues[selectedValues.length - 1];
      if (lastValue) {
        removeValue(lastValue);
      }
    }
    // Prevent escape from propagating to close the popover immediately
    // Let the command handle it first
    if (event.key === "Escape") {
      event.stopPropagation();
    }
  }, [searchValue, selectedValues, removeValue]);

  const displayLabels = selectedValues
    .map((val) => options.find((opt) => opt.value === val)?.label ?? val)
    .filter(Boolean);

  const lineCount = getLineCount(rowHeight);

  const { visibleItems: visibleLabels, hiddenCount: hiddenBadgeCount } =
    useBadgeOverflow({
      items: displayLabels,
      getLabel: (label) => label,
      containerRef,
      lineCount,
    });

  return (
    <DataGridCellWrapper
      ref={containerRef}
      cell={cell}
      tableMeta={tableMeta}
      rowIndex={rowIndex}
      columnId={columnId}
      rowHeight={rowHeight}
      isEditing={isEditing}
      isFocused={isFocused}
      isSelected={isSelected}
      isSearchMatch={isSearchMatch}
      isActiveSearchMatch={isActiveSearchMatch}
      readOnly={readOnly}
      onKeyDown={onWrapperKeyDown}>
      {isEditing ? (
        <Popover open={isEditing} onOpenChange={onOpenChange}>
          <PopoverAnchor asChild>
            <div className="absolute inset-0" />
          </PopoverAnchor>
          <PopoverContent
            data-grid-cell-editor=""
            align="start"
            sideOffset={sideOffset}
            className="w-[300px] rounded-none p-0"
            onOpenAutoFocus={onOpenAutoFocus}>
            <Command
              className="**:data-[slot=command-input-wrapper]:h-auto **:data-[slot=command-input-wrapper]:border-none **:data-[slot=command-input-wrapper]:p-0 [&_[data-slot=command-input-wrapper]_svg]:hidden">
              <div
                className="flex min-h-9 flex-wrap items-center gap-1 border-b px-3 py-1.5">
                {selectedValues.map((value) => {
                  const option = options.find((opt) => opt.value === value);
                  const label = option?.label ?? value;

                  return (
                    <Badge key={value} variant="secondary" className="h-5 gap-1 px-1.5 text-xs">
                      {label}
                      <button
                        type="button"
                        onClick={(event) => removeValue(value, event)}
                        onPointerDown={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                        }}>
                        <X className="size-3" />
                      </button>
                    </Badge>
                  );
                })}
                <CommandInput
                  ref={inputRef}
                  value={searchValue}
                  onValueChange={setSearchValue}
                  onKeyDown={onInputKeyDown}
                  placeholder="Search..."
                  className="h-auto flex-1 p-0" />
              </div>
              <CommandList className="max-h-full">
                <CommandEmpty>No options found.</CommandEmpty>
                <CommandGroup className="max-h-[300px] scroll-py-1 overflow-y-auto overflow-x-hidden">
                  {options.map((option) => {
                    const isSelected = selectedValues.includes(option.value);

                    return (
                      <CommandItem
                        key={option.value}
                        value={option.label}
                        onSelect={() => onValueChange(option.value)}>
                        <div
                          className={cn(
                            "flex size-4 items-center justify-center rounded-sm border border-primary",
                            isSelected
                              ? "bg-primary text-primary-foreground"
                              : "opacity-50 [&_svg]:invisible"
                          )}>
                          <Check className="size-3" />
                        </div>
                        <span>{option.label}</span>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
                {selectedValues.length > 0 && (
                  <>
                    <CommandSeparator />
                    <CommandGroup>
                      <CommandItem onSelect={clearAll} className="justify-center text-muted-foreground">
                        Clear all
                      </CommandItem>
                    </CommandGroup>
                  </>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      ) : null}
      {displayLabels.length > 0 ? (
        <div className="flex flex-wrap items-center gap-1 overflow-hidden">
          {visibleLabels.map((label, index) => (
            <Badge
              key={selectedValues[index]}
              variant="secondary"
              className="h-5 shrink-0 px-1.5 text-xs">
              {label}
            </Badge>
          ))}
          {hiddenBadgeCount > 0 && (
            <Badge
              variant="outline"
              className="h-5 shrink-0 px-1.5 text-muted-foreground text-xs">
              +{hiddenBadgeCount}
            </Badge>
          )}
        </div>
      ) : null}
    </DataGridCellWrapper>
  );
}

function formatDateForDisplay(dateStr) {
  if (!dateStr) return "";

  // Parse YYYY-MM-DD as a local date (avoids timezone shifting)
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
  const date = m
    ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
    : new Date(dateStr);

  if (Number.isNaN(date.getTime())) return "";

  // e.g. "Dec 16" (US) / "16 Dec" (UK) depending on user locale
  return new Intl.DateTimeFormat(undefined, { month: "2-digit", day: "numeric" }).format(date);
}

export function DateCell(
  {
    cell,
    tableMeta,
    rowIndex,
    columnId,
    rowHeight,
    isFocused,
    isEditing,
    isSelected,
    isSearchMatch,
    isActiveSearchMatch,
    readOnly
  }
) {
  const initialValue = cell.getValue();
  const [value, setValue] = React.useState(initialValue ?? "");
  const containerRef = React.useRef(null);

  const prevInitialValueRef = React.useRef(initialValue);
  if (initialValue !== prevInitialValueRef.current) {
    prevInitialValueRef.current = initialValue;
    setValue(initialValue ?? "");
  }

  const selectedDate = value ? new Date(value) : undefined;

  const onDateSelect = React.useCallback((date) => {
    if (!date || readOnly) return;

    const formattedDate = date.toISOString().split("T")[0] ?? "";
    setValue(formattedDate);
    tableMeta?.onDataUpdate?.({ rowIndex, columnId, value: formattedDate });
    tableMeta?.onCellEditingStop?.();
  }, [tableMeta, rowIndex, columnId, readOnly]);

  const onOpenChange = React.useCallback((isOpen) => {
    if (isOpen && !readOnly) {
      tableMeta?.onCellEditingStart?.(rowIndex, columnId);
    } else {
      tableMeta?.onCellEditingStop?.();
    }
  }, [tableMeta, rowIndex, columnId, readOnly]);

  const onWrapperKeyDown = React.useCallback((event) => {
    if (isEditing && event.key === "Escape") {
      event.preventDefault();
      setValue(initialValue);
      tableMeta?.onCellEditingStop?.();
    } else if (!isEditing && isFocused && event.key === "Tab") {
      event.preventDefault();
      tableMeta?.onCellEditingStop?.({
        direction: event.shiftKey ? "left" : "right",
      });
    }
  }, [isEditing, isFocused, initialValue, tableMeta]);

  return (
    <DataGridCellWrapper
      ref={containerRef}
      cell={cell}
      tableMeta={tableMeta}
      rowIndex={rowIndex}
      columnId={columnId}
      rowHeight={rowHeight}
      isEditing={isEditing}
      isFocused={isFocused}
      isSelected={isSelected}
      isSearchMatch={isSearchMatch}
      isActiveSearchMatch={isActiveSearchMatch}
      readOnly={readOnly}
      onKeyDown={onWrapperKeyDown}>
      <Popover open={isEditing} onOpenChange={onOpenChange}>
        <PopoverAnchor asChild>
          <span data-slot="grid-cell-content">
            {formatDateForDisplay(value)}
          </span>
        </PopoverAnchor>
        {isEditing && (
          <PopoverContent
            data-grid-cell-editor=""
            align="start"
            alignOffset={-8}
            className="w-auto p-0">
            <Calendar
              autoFocus
              captionLayout="dropdown"
              mode="single"
              defaultMonth={selectedDate ?? new Date()}
              selected={selectedDate}
              onSelect={onDateSelect} />
          </PopoverContent>
        )}
      </Popover>
    </DataGridCellWrapper>
  );
}

function formatFileSize(bytes) {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${Number.parseFloat((bytes / k ** i).toFixed(1))} ${sizes[i]}`;
}

function getFileIcon(type) {
  if (type.startsWith("image/")) return FileImage;
  if (type.startsWith("video/")) return FileVideo;
  if (type.startsWith("audio/")) return FileAudio;
  if (type.includes("pdf")) return FileText;
  if (type.includes("zip") || type.includes("rar")) return FileArchive;
  if (
    type.includes("word") ||
    type.includes("document") ||
    type.includes("doc")
  )
    return FileText;
  if (type.includes("sheet") || type.includes("excel") || type.includes("xls"))
    return FileSpreadsheet;
  if (
    type.includes("presentation") ||
    type.includes("powerpoint") ||
    type.includes("ppt")
  )
    return Presentation;
  return File;
}

export function FileCell(
  {
    cell,
    tableMeta,
    rowIndex,
    columnId,
    rowHeight,
    isFocused,
    isEditing,
    isSelected,
    isSearchMatch,
    isActiveSearchMatch,
    readOnly
  }
) {
  const cellValue = React.useMemo(() => (cell.getValue()) ?? [], [cell]);

  const cellKey = getCellKey(rowIndex, columnId);
  const prevCellKeyRef = React.useRef(cellKey);

  const labelId = React.useId();
  const descriptionId = React.useId();

  const [files, setFiles] = React.useState(cellValue);
  const [uploadingFiles, setUploadingFiles] = React.useState(new Set());
  const [deletingFiles, setDeletingFiles] = React.useState(new Set());
  const [isDraggingOver, setIsDraggingOver] = React.useState(false);
  const [isDragging, setIsDragging] = React.useState(false);
  const [error, setError] = React.useState(null);

  const isUploading = uploadingFiles.size > 0;
  const isDeleting = deletingFiles.size > 0;
  const isPending = isUploading || isDeleting;
  const containerRef = React.useRef(null);
  const fileInputRef = React.useRef(null);
  const dropzoneRef = React.useRef(null);
  const cellOpts = cell.column.columnDef.meta?.cell;
  const sideOffset = -(containerRef.current?.clientHeight ?? 0);

  const fileCellOpts = cellOpts?.variant === "file" ? cellOpts : null;
  const maxFileSize = fileCellOpts?.maxFileSize ?? 10 * 1024 * 1024;
  const maxFiles = fileCellOpts?.maxFiles ?? 10;
  const accept = fileCellOpts?.accept;
  const multiple = fileCellOpts?.multiple ?? false;

  const acceptedTypes = React.useMemo(() => (accept ? accept.split(",").map((t) => t.trim()) : null), [accept]);

  const prevCellValueRef = React.useRef(cellValue);
  if (cellValue !== prevCellValueRef.current) {
    prevCellValueRef.current = cellValue;
    for (const file of files) {
      if (file.url) {
        URL.revokeObjectURL(file.url);
      }
    }
    setFiles(cellValue);
    setError(null);
  }

  if (prevCellKeyRef.current !== cellKey) {
    prevCellKeyRef.current = cellKey;
    setError(null);
  }

  const validateFile = React.useCallback(file => {
    if (maxFileSize && file.size > maxFileSize) {
      return `File size exceeds ${formatFileSize(maxFileSize)}`;
    }
    if (acceptedTypes) {
      const fileExtension = `.${file.name.split(".").pop()}`;
      const isAccepted = acceptedTypes.some((type) => {
        if (type.endsWith("/*")) {
          const baseType = type.slice(0, -2);
          return file.type.startsWith(`${baseType}/`);
        }
        if (type.startsWith(".")) {
          return fileExtension.toLowerCase() === type.toLowerCase();
        }
        return file.type === type;
      });
      if (!isAccepted) {
        return "File type not accepted";
      }
    }
    return null;
  }, [maxFileSize, acceptedTypes]);

  const addFiles = React.useCallback(async (newFiles, skipUpload = false) => {
    if (readOnly || isPending) return;
    setError(null);

    if (maxFiles && files.length + newFiles.length > maxFiles) {
      const errorMessage = `Maximum ${maxFiles} files allowed`;
      setError(errorMessage);
      toast(errorMessage);
      setTimeout(() => {
        setError(null);
      }, 2000);
      return;
    }

    const rejectedFiles = [];
    const filesToValidate = [];

    for (const file of newFiles) {
      const validationError = validateFile(file);
      if (validationError) {
        rejectedFiles.push({ name: file.name, reason: validationError });
        continue;
      }
      filesToValidate.push(file);
    }

    if (rejectedFiles.length > 0) {
      const firstError = rejectedFiles[0];
      if (firstError) {
        setError(firstError.reason);

        const truncatedName =
          firstError.name.length > 20
            ? `${firstError.name.slice(0, 20)}...`
            : firstError.name;

        if (rejectedFiles.length === 1) {
          toast(firstError.reason, {
            description: `"${truncatedName}" has been rejected`,
          });
        } else {
          toast(firstError.reason, {
            description: `"${truncatedName}" and ${rejectedFiles.length - 1} more rejected`,
          });
        }

        setTimeout(() => {
          setError(null);
        }, 2000);
      }
    }

    if (filesToValidate.length > 0) {
      if (!skipUpload) {
        const tempFiles = filesToValidate.map((f) => ({
          id: crypto.randomUUID(),
          name: f.name,
          size: f.size,
          type: f.type,
          url: undefined,
        }));
        const filesWithTemp = [...files, ...tempFiles];
        setFiles(filesWithTemp);

        const uploadingIds = new Set(tempFiles.map((f) => f.id));
        setUploadingFiles(uploadingIds);

        let uploadedFiles = [];

        if (tableMeta?.onFilesUpload) {
          try {
            uploadedFiles = await tableMeta.onFilesUpload({
              files: filesToValidate,
              rowIndex,
              columnId,
            });
          } catch (error) {
            toast.error(error instanceof Error
              ? error.message
              : `Failed to upload ${filesToValidate.length} file${filesToValidate.length !== 1 ? "s" : ""}`);
            setFiles((prev) => prev.filter((f) => !uploadingIds.has(f.id)));
            setUploadingFiles(new Set());
            return;
          }
        } else {
          uploadedFiles = filesToValidate.map((f, i) => ({
            id: tempFiles[i]?.id ?? crypto.randomUUID(),
            name: f.name,
            size: f.size,
            type: f.type,
            url: URL.createObjectURL(f),
          }));
        }

        const finalFiles = filesWithTemp
          .map((f) => {
            if (uploadingIds.has(f.id)) {
              return uploadedFiles.find((uf) => uf.name === f.name) ?? f;
            }
            return f;
          })
          .filter((f) => f.url !== undefined);

        setFiles(finalFiles);
        setUploadingFiles(new Set());
        tableMeta?.onDataUpdate?.({ rowIndex, columnId, value: finalFiles });
      } else {
        const newFilesData = filesToValidate.map((f) => ({
          id: crypto.randomUUID(),
          name: f.name,
          size: f.size,
          type: f.type,
          url: URL.createObjectURL(f),
        }));
        const updatedFiles = [...files, ...newFilesData];
        setFiles(updatedFiles);
        tableMeta?.onDataUpdate?.({
          rowIndex,
          columnId,
          value: updatedFiles,
        });
      }
    }
  }, [
    files,
    maxFiles,
    validateFile,
    tableMeta,
    rowIndex,
    columnId,
    readOnly,
    isPending,
  ]);

  const removeFile = React.useCallback(async (fileId) => {
    if (readOnly || isPending) return;
    setError(null);

    const fileToRemove = files.find((f) => f.id === fileId);
    if (!fileToRemove) return;

    setDeletingFiles((prev) => new Set(prev).add(fileId));

    if (tableMeta?.onFilesDelete) {
      try {
        await tableMeta.onFilesDelete({
          fileIds: [fileId],
          rowIndex,
          columnId,
        });
      } catch (error) {
        toast.error(error instanceof Error
          ? error.message
          : `Failed to delete ${fileToRemove.name}`);
        setDeletingFiles((prev) => {
          const next = new Set(prev);
          next.delete(fileId);
          return next;
        });
        return;
      }
    }

    if (fileToRemove.url?.startsWith("blob:")) {
      URL.revokeObjectURL(fileToRemove.url);
    }

    const updatedFiles = files.filter((f) => f.id !== fileId);
    setFiles(updatedFiles);
    setDeletingFiles((prev) => {
      const next = new Set(prev);
      next.delete(fileId);
      return next;
    });
    tableMeta?.onDataUpdate?.({ rowIndex, columnId, value: updatedFiles });
  }, [files, tableMeta, rowIndex, columnId, readOnly, isPending]);

  const clearAll = React.useCallback(async () => {
    if (readOnly || isPending) return;
    setError(null);

    const fileIds = files.map((f) => f.id);
    setDeletingFiles(new Set(fileIds));

    if (tableMeta?.onFilesDelete && files.length > 0) {
      try {
        await tableMeta.onFilesDelete({
          fileIds,
          rowIndex,
          columnId,
        });
      } catch (error) {
        toast.error(error instanceof Error ? error.message : "Failed to delete files");
        setDeletingFiles(new Set());
        return;
      }
    }

    for (const file of files) {
      if (file.url?.startsWith("blob:")) {
        URL.revokeObjectURL(file.url);
      }
    }
    setFiles([]);
    setDeletingFiles(new Set());
    tableMeta?.onDataUpdate?.({ rowIndex, columnId, value: [] });
  }, [files, tableMeta, rowIndex, columnId, readOnly, isPending]);

  const onCellDragEnter = React.useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer.types.includes("Files")) {
      setIsDraggingOver(true);
    }
  }, []);

  const onCellDragLeave = React.useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    const x = event.clientX;
    const y = event.clientY;

    if (
      x <= rect.left ||
      x >= rect.right ||
      y <= rect.top ||
      y >= rect.bottom
    ) {
      setIsDraggingOver(false);
    }
  }, []);

  const onCellDragOver = React.useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const onCellDrop = React.useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDraggingOver(false);

    const droppedFiles = Array.from(event.dataTransfer.files);
    if (droppedFiles.length > 0) {
      addFiles(droppedFiles, false);
    }
  }, [addFiles]);

  const onDropzoneDragEnter = React.useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  }, []);

  const onDropzoneDragLeave = React.useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    const x = event.clientX;
    const y = event.clientY;

    if (
      x <= rect.left ||
      x >= rect.right ||
      y <= rect.top ||
      y >= rect.bottom
    ) {
      setIsDragging(false);
    }
  }, []);

  const onDropzoneDragOver = React.useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const onDropzoneDrop = React.useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);

    const droppedFiles = Array.from(event.dataTransfer.files);
    addFiles(droppedFiles, false);
  }, [addFiles]);

  const onDropzoneClick = React.useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const onDropzoneKeyDown = React.useCallback((event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onDropzoneClick();
    }
  }, [onDropzoneClick]);

  const onFileInputChange = React.useCallback((event) => {
    const selectedFiles = Array.from(event.target.files ?? []);
    addFiles(selectedFiles, false);
    event.target.value = "";
  }, [addFiles]);

  const onOpenChange = React.useCallback((isOpen) => {
    if (isOpen && !readOnly) {
      setError(null);
      tableMeta?.onCellEditingStart?.(rowIndex, columnId);
    } else {
      setError(null);
      tableMeta?.onCellEditingStop?.();
    }
  }, [tableMeta, rowIndex, columnId, readOnly]);

  const onEscapeKeyDown = React.useCallback((event) => {
    // Prevent the escape key from propagating to the data grid's keyboard handler
    // which would call blurCell() and remove focus from the cell
    event.stopPropagation();
  }, []);

  const onOpenAutoFocus = React.useCallback((event) => {
    event.preventDefault();
    queueMicrotask(() => {
      dropzoneRef.current?.focus();
    });
  }, []);

  const onWrapperKeyDown = React.useCallback((event) => {
    if (isEditing) {
      if (event.key === "Escape") {
        event.preventDefault();
        setFiles(cellValue);
        setError(null);
        tableMeta?.onCellEditingStop?.();
      } else if (event.key === " ") {
        event.preventDefault();
        onDropzoneClick();
      }
    } else if (isFocused && event.key === "Enter") {
      event.preventDefault();
      tableMeta?.onCellEditingStart?.(rowIndex, columnId);
    } else if (!isEditing && isFocused && event.key === "Tab") {
      event.preventDefault();
      tableMeta?.onCellEditingStop?.({
        direction: event.shiftKey ? "left" : "right",
      });
    }
  }, [
    isEditing,
    isFocused,
    cellValue,
    tableMeta,
    onDropzoneClick,
    rowIndex,
    columnId,
  ]);

  React.useEffect(() => {
    return () => {
      for (const file of files) {
        if (file.url) {
          URL.revokeObjectURL(file.url);
        }
      }
    };
  }, [files]);

  const lineCount = getLineCount(rowHeight);

  const { visibleItems: visibleFiles, hiddenCount: hiddenFileCount } =
    useBadgeOverflow({
      items: files,
      getLabel: (file) => file.name,
      containerRef,
      lineCount,
      cacheKeyPrefix: "file",
      iconSize: 12,
      maxWidth: 100,
    });

  return (
    <DataGridCellWrapper
      ref={containerRef}
      cell={cell}
      tableMeta={tableMeta}
      rowIndex={rowIndex}
      columnId={columnId}
      rowHeight={rowHeight}
      isEditing={isEditing}
      isFocused={isFocused}
      isSelected={isSelected}
      isSearchMatch={isSearchMatch}
      isActiveSearchMatch={isActiveSearchMatch}
      readOnly={readOnly}
      className={cn({
        "ring-1 ring-primary/80 ring-inset": isDraggingOver,
      })}
      onDragEnter={onCellDragEnter}
      onDragLeave={onCellDragLeave}
      onDragOver={onCellDragOver}
      onDrop={onCellDrop}
      onKeyDown={onWrapperKeyDown}>
      {isEditing ? (
        <Popover open={isEditing} onOpenChange={onOpenChange}>
          <PopoverAnchor asChild>
            <div className="absolute inset-0" />
          </PopoverAnchor>
          <PopoverContent
            data-grid-cell-editor=""
            align="start"
            sideOffset={sideOffset}
            className="w-[400px] rounded-none p-0"
            onEscapeKeyDown={onEscapeKeyDown}
            onOpenAutoFocus={onOpenAutoFocus}>
            <div className="flex flex-col gap-2 p-3">
              <span id={labelId} className="sr-only">
                File upload
              </span>
              <div
                role="region"
                aria-labelledby={labelId}
                aria-describedby={descriptionId}
                aria-invalid={!!error}
                aria-disabled={isPending}
                data-dragging={isDragging ? "" : undefined}
                data-invalid={error ? "" : undefined}
                data-disabled={isPending ? "" : undefined}
                tabIndex={isDragging || isPending ? -1 : 0}
                className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed p-6 outline-none transition-colors hover:bg-accent/30 focus-visible:border-ring/50 data-disabled:pointer-events-none data-dragging:border-primary/30 data-invalid:border-destructive data-dragging:bg-accent/30 data-disabled:opacity-50 data-invalid:ring-destructive/20"
                ref={dropzoneRef}
                onClick={onDropzoneClick}
                onDragEnter={onDropzoneDragEnter}
                onDragLeave={onDropzoneDragLeave}
                onDragOver={onDropzoneDragOver}
                onDrop={onDropzoneDrop}
                onKeyDown={onDropzoneKeyDown}>
                <Upload className="size-8 text-muted-foreground" />
                <div className="text-center text-sm">
                  <p className="font-medium">
                    {isDragging ? "Drop files here" : "Drag files here"}
                  </p>
                  <p className="text-muted-foreground text-xs">
                    or click to browse
                  </p>
                </div>
                <p id={descriptionId} className="text-muted-foreground text-xs">
                  {maxFileSize
                    ? `Max size: ${formatFileSize(maxFileSize)}${maxFiles ? ` • Max ${maxFiles} files` : ""}`
                    : maxFiles
                      ? `Max ${maxFiles} files`
                      : "Select files to upload"}
                </p>
              </div>
              <input
                type="file"
                aria-labelledby={labelId}
                aria-describedby={descriptionId}
                multiple={multiple}
                accept={accept}
                className="sr-only"
                ref={fileInputRef}
                onChange={onFileInputChange} />
              {files.length > 0 && (
                <div className="flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <p className="font-medium text-muted-foreground text-xs">
                      {files.length} {files.length === 1 ? "file" : "files"}
                    </p>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-6 text-muted-foreground text-xs"
                      onClick={clearAll}
                      disabled={isPending}>
                      Clear all
                    </Button>
                  </div>
                  <div className="max-h-[200px] space-y-1 overflow-y-auto">
                    {files.map((file) => {
                      const FileIcon = getFileIcon(file.type);
                      const isFileUploading = uploadingFiles.has(file.id);
                      const isFileDeleting = deletingFiles.has(file.id);
                      const isFilePending = isFileUploading || isFileDeleting;

                      return (
                        <div
                          key={file.id}
                          data-pending={isFilePending ? "" : undefined}
                          className="flex items-center gap-2 rounded-md border bg-muted/50 px-2 py-1.5 data-pending:opacity-60">
                          {FileIcon && (
                            <FileIcon className="size-4 shrink-0 text-muted-foreground" />
                          )}
                          <div className="flex-1 overflow-hidden">
                            <p className="truncate text-sm">{file.name}</p>
                            <p className="text-muted-foreground text-xs">
                              {isFileUploading
                                ? "Uploading..."
                                : isFileDeleting
                                  ? "Deleting..."
                                  : formatFileSize(file.size)}
                            </p>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="size-5 rounded-sm"
                            onClick={() => removeFile(file.id)}
                            disabled={isPending}>
                            <X className="size-3" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </PopoverContent>
        </Popover>
      ) : null}
      {isDraggingOver ? (
        <div className="flex items-center justify-center gap-2 text-primary text-sm">
          <Upload className="size-4" />
          <span>Drop files here</span>
        </div>
      ) : files.length > 0 ? (
        <div className="flex flex-wrap items-center gap-1 overflow-hidden">
          {visibleFiles.map((file) => {
            const isUploading = uploadingFiles.has(file.id);

            if (isUploading) {
              // Show skeleton for uploading files
              return (
                <Skeleton
                  key={file.id}
                  className="h-5 shrink-0 px-1.5"
                  style={{
                    width: `${Math.min(file.name.length * 8 + 30, 100)}px`,
                  }} />
              );
            }

            return (
              <Badge
                key={file.id}
                variant="secondary"
                className="h-5 shrink-0 gap-1 px-1.5 text-xs">
                {React.createElement(getFileIcon(file.type), {
                  className: "size-3 shrink-0",
                })}
                <span className="max-w-[100px] truncate">{file.name}</span>
              </Badge>
            );
          })}
          {hiddenFileCount > 0 && (
            <Badge
              variant="outline"
              className="h-5 shrink-0 px-1.5 text-muted-foreground text-xs">
              +{hiddenFileCount}
            </Badge>
          )}
        </div>
      ) : null}
    </DataGridCellWrapper>
  );
}
